import { ENUM__html_ref_stub } from '@/enums/ENUM__html_ref_stub';
import { use_ref_with_fallback } from '@/hooks/_base/use_ref_with_fallback';
import { use_component_is_mounted_300 } from '@/hooks/is_mounted/use_component_is_mounted_300';
import { use_slice_workspace } from '@/hooks/store/use_slices';
import { UserAvatarReadOnly } from '@/pages/+settings/workspace/_base/UserAvatarReadOnly';
import { get_entity_viewer_color_as_workspace_user } from '@/utils/utils_client_workspace_child_entity_viewers';
import { DTO__entity_with_grid2d } from '@shared/dto/+workspace/workspace_entity/_database_entity_with_grid2d_permissions/DTO__entity_with_grid2d';
import { ENUM__workspace_entity_viewer } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_viewer';
import { DTOWorkspaceChildEntityViewer } from '@shared/dto/+workspace/workspace_entity/dto_workspace_child_entity_viewer';
import { chunk_array } from '@shared/utils/utils_array';
import { useState } from 'react';
import { BarGrid2dViewersChunk } from './BarGrid2dViewersChunk';
import { BarGrid2dViewersDropdown } from './BarGrid2dViewersDropdown';

const CHUNK_SIZE = 5;
const MAX_VISIBLE_USERS = CHUNK_SIZE - 1;

interface Props {
  entity: DTO__entity_with_grid2d;
}

export function GRID2D_BAR_viewers({ entity }: Props) {
  /*
   * STORE
   */
  const { child_entity_viewers_map, current_workspace_user, workspace_current, all_workspaces_users_map } = use_slice_workspace([
    'child_entity_viewers_map',
    'current_workspace_user',
    'workspace_current',
    'all_workspaces_users_map',
  ]);

  const { is_mounted } = use_component_is_mounted_300();

  const ref_trigger = use_ref_with_fallback(ENUM__html_ref_stub.DIV);

  /*
   * STATE
   */

  const [isDropdownOpen, setDropdownOpen] = useState(false);

  const workspace_users_map = all_workspaces_users_map.get_value_or_default(workspace_current._id, new Map());
  const entity_viewers = Array.from(child_entity_viewers_map.values()).filter(
    item => item.entity_id === entity._id.toString() && item.entity_type === entity.type,
  );

  const current_user_id = current_workspace_user._id || current_workspace_user.user_id;
  const all_viewer_user_ids = [...new Set([...entity_viewers.map(v => v.user_id), current_workspace_user.user_id])].sort((a, b) => b.localeCompare(a));
  const workspace_viewer_ids = all_viewer_user_ids.filter(id => workspace_users_map.has(id));

  const categorized_viewers = (type: string) => entity_viewers.filter(v => v.viewer_type === type && v.get_viewer_id() !== current_user_id);

  const [workspace_users, ud_users, guests] = [
    categorized_viewers(ENUM__workspace_entity_viewer.WORKSPACE_USER),
    categorized_viewers(ENUM__workspace_entity_viewer.APP_USER),
    categorized_viewers(ENUM__workspace_entity_viewer.GUEST),
  ];

  const split_viewers = (viewers: DTOWorkspaceChildEntityViewer[]) => [viewers.slice(0, MAX_VISIBLE_USERS), viewers.slice(MAX_VISIBLE_USERS)];

  const [visible_workspace_users, remain_workspace_users] = split_viewers(workspace_users);
  const [visible_ud_users, remain_ud_users] = split_viewers(ud_users);
  const [visible_guests, remain_guests] = split_viewers(guests);

  /*
   * RENDERS
   */

  const render_chunk = (viewers: DTOWorkspaceChildEntityViewer[][]) => <BarGrid2dViewersChunk viewers={viewers} workspace_viewers_ids={workspace_viewer_ids} />;
  const chunk_and_render = (viewers: DTOWorkspaceChildEntityViewer[]) => (viewers.length ? render_chunk(chunk_array(viewers, CHUNK_SIZE)) : null);

  function renderDropdown(isVisible: boolean, length: number) {
    if (!isVisible) return null;

    const rendered_guests = workspace_users.length === 0 && ud_users.length === 0 ? remain_guests : guests;

    const rendered_ud_users = workspace_users.length === 0 ? remain_ud_users : ud_users;

    return (
      <BarGrid2dViewersDropdown length={length} isDropdownOpen={isDropdownOpen} ref_trigger={ref_trigger}>
        {chunk_and_render(rendered_guests)}
        {chunk_and_render(rendered_ud_users)}
        {chunk_and_render(remain_workspace_users)}
      </BarGrid2dViewersDropdown>
    );
  }

  function render_avatars(is_visible: boolean) {
    const users = visible_workspace_users.length > 0 ? visible_workspace_users : visible_ud_users.length > 0 ? visible_ud_users : visible_guests;
    const missingCount = is_visible ? Math.max(0, MAX_VISIBLE_USERS - users.length) : 0;

    return (
      <>
        {users.map(viewer => (
          <UserAvatarReadOnly
            key={viewer.get_viewer_id()}
            url_relative={viewer.user_avatar_url_relative}
            userName={viewer.user_name}
            className="!size-5"
            style={{ borderColor: `var(--user-color-${get_entity_viewer_color_as_workspace_user(viewer, workspace_viewer_ids)})` }}
          />
        ))}
        {[...Array(missingCount)].map((_, index) => (
          <div key={`placeholder-${index}`} className="!size-5" />
        ))}
      </>
    );
  }

  let hidden_avatars: number = 0;

  //if remain_workspace_users.length > 0, we must add remain_workspace_users.length to hidden_avatars, else we keep hidden_avatars empty
  hidden_avatars = remain_workspace_users.length > 0 ? hidden_avatars + remain_workspace_users.length : hidden_avatars;

  //if workspace_users.length === 0, we must add remain_ud_users.length to hidden_avatars, else we must add ud_users.length to hidden_avatars
  hidden_avatars = workspace_users.length === 0 ? hidden_avatars + remain_ud_users.length : hidden_avatars + ud_users.length;

  //if workspace_users.length === 0 and ud_users.length === 0, we must add remain_guests.length to hidden_avatars, else we must add guests.length to hidden_avatars
  hidden_avatars = workspace_users.length === 0 && ud_users.length === 0 ? hidden_avatars + remain_guests.length : hidden_avatars + guests.length;

  const isLeftoverVisible = hidden_avatars > 0;

  /*
   * HTML
   */

  if (!entity_viewers.length) return null;

  return (
    <div
      ref={ref_trigger}
      onMouseEnter={() => setDropdownOpen(true)}
      onMouseLeave={() => setDropdownOpen(false)}
      className={`bg-bar border border-component-border flex items-center space-x-2 px-2 py-1 rounded-lg hover-animation
        ${isLeftoverVisible ? 'min-w-[148px]' : ''} 
        ${is_mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}
    >
      {render_avatars(isLeftoverVisible)}
      {renderDropdown(isLeftoverVisible, hidden_avatars)}
    </div>
  );
}
